// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { __decorate } from 'tslib';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import '../../styles';
import { baseFontSize } from '../../utils/base-font-size';
import { event, ShuiLitElement } from '../../utils/event-decorator.js';
import { LeftArrowClickEvent, RightArrowClickEvent } from '../../utils/events.js';
import { ThrottlingMixin } from '../../utils/throttling-mixin.js';
import carouselStyles from './sh-carousel.lit.scss.js';
/**
 * @slot - The slot where the main content is rendered. Takes card, thumbnail and images as child.
 * @fires left-arrow-click {LeftArrowClickEvent} - Emitted when the left arrow is clicked.
 * @fires right-arrow-click {RightArrowClickEvent} - Emitted when the right arrow is clicked.
 */
let SHCarousel = class SHCarousel extends ThrottlingMixin(ShuiLitElement) {
  constructor() {
    super(...arguments);
    /** Defines the width of the slotted components in "rem", "%", "px", etc. Please note for images, a change in width will also result a change in height if not specified. */
    this.elementWidth = '30%';
    /** Defines the space between elements inside the carousel in "rem", "px", etc. */
    this.gap = '0.5rem';
    /** Defines how elements inside the carousel snap to the container when scrolled. Possible values are `start`, `center`, `end`, `none`. */
    this.snap = 'start';
    /**Accepts 'numeric' (default) or 'dots' Defaults to numeric. If set to dots, displays dot navigation. */
    this.counterType = 'numeric';
    /**Accepts 'floating' (default), 'footer-left' or 'footer-right'. */
    this.arrowPlacement = 'floating';
    /** Defines the item currently in focus. */
    this._currentItem = 0;
    /** Defines the total number of elements the carousel component has. */
    this._totalSlotElements = 0;
    this.resizeObserver = new ResizeObserver(this.slotResizeObserverCallback.bind(this));
    this.currentScrollPosition = 0; // Track the intended scroll position
    this.isScrolling = true;
    this.allItemsVisible = false;
  }
  static get styles() {
    return [carouselStyles];
  }
  render() {
    var _a, _b, _c, _d;
    const startDot = this.calculateStartDot();
    const prevDisabled = this._currentItem <= 1 || this.allItemsVisible;
    const nextDisabled = this._currentItem === this._totalSlotElements || this.reachedEnd;
    return html`
      <div style="width: 100%; display: flex; flex-direction: column;">
        <slot
          aria-live="polite"
          id="slot"
          style="${styleMap(this.getDimensionStyles())}"
          @scroll="${this.throttleScroll}"
          @slotchange="${(e) => this.contentSlotChange(e)}"
        ></slot>
        <div class="counter-wrapper">
          ${this.counter
            ? html` <div class="counter">
                ${this.counterType === 'numeric' || (this.counter && this.counterType !== 'dots')
                  ? `${this._currentItem} / ${this._totalSlotElements}`
                  : html`<div
                      class="dots"
                      style="transform: translateX(${-startDot *
                      parseFloat(getComputedStyle(this).getPropertyValue('--carousel-dot-height')) *
                      baseFontSize}px)"
                    >
                      ${this.getDots()}
                    </div>`}
              </div>`
            : ''}
          ${this.arrows
            ? html`${this.arrowPlacement !== 'footer-left' && this.arrowPlacement !== 'footer-right'
                ? html`
                    <sh-icon
                      button
                      ?hidden="${prevDisabled}"
                      ?disabled="${prevDisabled}"
                      aria-label="${(_a = this.accessiblePreviousLabel) !== null && _a !== void 0
                        ? _a
                        : 'Previous'}"
                      icon="arrow-left-s"
                      size="m"
                      class="arrow left"
                      @click="${() => this.scrollToItem(this._currentItem, -1)}"
                    ></sh-icon>
                    <sh-icon
                      button
                      ?hidden="${nextDisabled}"
                      ?disabled="${nextDisabled}"
                      aria-label="${(_b = this.accessibleNextLabel) !== null && _b !== void 0
                        ? _b
                        : 'Next'}"
                      icon="arrow-right-s"
                      @click="${() => this.scrollToItem(this._currentItem, 1)}"
                      class="arrow right"
                    ></sh-icon>
                  `
                : html`<div class="button-arrow-wrapper">
                    <sh-button
                      aria-label="${(_c = this.accessiblePreviousLabel) !== null && _c !== void 0
                        ? _c
                        : 'Previous'}"
                      icon="arrow-line-left-s"
                      class="button-arrow left"
                      @click="${() => this.scrollToItem(this._currentItem, -1)}"
                      .disabled="${prevDisabled}"
                      layout="normal"
                      color="secondary"
                    ></sh-button>
                    <sh-button
                      aria-label="${(_d = this.accessibleNextLabel) !== null && _d !== void 0
                        ? _d
                        : 'Next'}"
                      icon="arrow-line-right-s"
                      class="button-arrow right"
                      .disabled="${nextDisabled}"
                      @click="${() => this.scrollToItem(this._currentItem, 1)}"
                      layout="normal"
                      color="secondary"
                    ></sh-button>
                  </div>`}`
            : ''}
        </div>
      </div>
    `;
  }
  calculateStartDot() {
    const totalVisibleDots = 6; // Number of visible dots
    const currentSelectedOffset = 4;
    const startDot = Math.max(
      0,
      Math.min(
        this._currentItem - currentSelectedOffset,
        this._totalSlotElements - totalVisibleDots
      )
    );
    return startDot;
  }
  getDots() {
    const startDot = this.calculateStartDot();
    const endDot = startDot + 6;
    const dotsArray = [];
    for (let i = 0; i < this._totalSlotElements; i++) {
      dotsArray.push(
        html` <div
          class="dot ${i} ${i + 1 === this._currentItem ? 'current-selected' : ''} ${i >=
            startDot && i < endDot
            ? 'in-view'
            : ''} ${(i === startDot && startDot > 0) ||
          (i === endDot - 1 && endDot < this._totalSlotElements)
            ? 'edge'
            : ''}"
        ></div>`
      );
    }
    return dotsArray;
  }
  connectedCallback() {
    super.connectedCallback();
    this.updateComplete.then(() => {
      var _a;
      const slot =
        (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('slot');
      this.resizeObserver.observe(slot);
    });
    this.updateVisibleAriaAttributes();
  }
  disconnectedCallback() {
    var _a;
    const slot =
      (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('slot');
    this.resizeObserver.unobserve(slot);
    this.resizeObserver.disconnect();
    super.disconnectedCallback();
  }
  update(changedProperties) {
    super.update(changedProperties);
    if (changedProperties.has('snap') && this.arrows) {
      this.snap = 'start';
    }
    if (changedProperties.has('arrows') && this.arrows && !(this.snap === 'start')) {
      this.snap = 'start';
    }
  }
  contentSlotChange(e) {
    const slot = e.target;
    const slotElements = slot.assignedElements({
      flatten: true,
    }).length;
    if (slotElements) {
      this._totalSlotElements = this.children.length;
      this.handleScroll();
    }
  }
  getDimensionStyles() {
    return {
      '--slot-component-width': this.elementWidth,
      gap: this.gap,
    };
  }
  handleScroll() {
    const slot = this.renderRoot.querySelector('slot');
    this.updateVisibleAriaAttributes();
    this.updateCurrentItemBasedOnSlot(slot);
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.isScrolling = true;
    }, 100);
  }
  throttleScroll() {
    // Throttle mixin
    this.throttle(this.handleScroll.bind(this), 50);
  }
  scrollToItem(itemIndex, addOrSubtract) {
    var _a;
    // scroll to a given item (start from 1) i.e given itemIndex becomes focusable.
    if (itemIndex < 1) {
      itemIndex = 1;
    }
    if (itemIndex > this.children.length) {
      itemIndex = this.children.length;
    }
    const slot =
      (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('slot');
    if (slot) {
      // Calculate the amount to scroll based on the width of the visible area (clientWidth)
      // and the direction of the scroll (addOrSubtract determines left or right scroll).
      const scrollAmount = slot.clientWidth * addOrSubtract;
      // Update the intended scroll position by adding the calculated scroll amount.
      // This ensures that multiple rapid clicks are cumulatively accounted for,
      // moving the slider further each time.
      this.currentScrollPosition += scrollAmount;
      // Ensure the updated scroll position stays within the valid boundaries of the scrollable area.
      // It prevents scrolling beyond the first slide (0) or past the last slide
      // (slot.scrollWidth - slot.clientWidth).
      this.currentScrollPosition = Math.max(
        0,
        Math.min(this.currentScrollPosition, slot.scrollWidth - slot.clientWidth)
      );
      this.isScrolling = false;
      this.reachedEnd = this.currentScrollPosition + slot.clientWidth >= slot.scrollWidth;
      this._currentItem = this.reachedEnd
        ? this._totalSlotElements
        : Math.floor(
            this.currentScrollPosition / (this.children[0].clientWidth + parseInt(this.gap, 10)) + 1
          );
      slot.scrollLeft += scrollAmount;
      // Perform the scroll to the calculated position with a smooth transition effect.
      // This moves the slider to the intended position based on user interactions.
      slot.scroll({
        left: this.currentScrollPosition,
        behavior: 'smooth',
      });
      this.updateVisibleAriaAttributes();
      // Dispatch custom events based on the direction of the scroll
      const details = { detail: { currentItem: itemIndex }, bubbles: true, composed: true };
      if (addOrSubtract === -1) {
        this.leftArrowClickEvent.emit(new LeftArrowClickEvent(details));
      } else {
        this.rightArrowClickEvent.emit(new RightArrowClickEvent(details));
      }
    }
  }
  /**
   * Updates the `aria-hidden` attribute for each carousel item based on its visibility
   * within the scrollable container. This improves screen reader support by ensuring
   * only the visible elements are announced.
   */
  updateVisibleAriaAttributes() {
    var _a;
    // Get the <slot> element that contains the carousel items
    const slot =
      (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('slot');
    if (!slot) return;
    // Get the visible area of the carousel container
    const containerRect = slot.getBoundingClientRect();
    // Loop through each slotted child (carousel item)
    Array.from(this.children).forEach((element) => {
      // Get the dimensions and position of the child relative to the viewport
      const elRect = element.getBoundingClientRect();
      // Check if the element is at least partially visible in the carousel viewport
      const isVisible = elRect.left < containerRect.right && elRect.right > containerRect.left;
      // Update the aria-hidden attribute to reflect visibility
      // Only visible elements should be exposed to screen readers
      element.setAttribute('aria-hidden', String(!isVisible));
    });
  }
  slotResizeObserverCallback(e) {
    const slot = e[0].target;
    this.updateCurrentItemBasedOnSlot(slot);
  }
  // This function updates the _currentItem when scrolled to the one which is focused.
  updateCurrentItemBasedOnSlot(slot) {
    const slotElementsPresent = slot.assignedElements({
      flatten: true,
    }).length;
    this.allItemsVisible = slot.scrollWidth <= slot.clientWidth;
    if (slotElementsPresent) {
      this.reachedEnd = slot.scrollLeft + slot.clientWidth >= slot.scrollWidth;
      if (this.isScrolling) {
        this._currentItem = this.reachedEnd
          ? this._totalSlotElements
          : Math.floor(
              slot.scrollLeft / (this.children[0].clientWidth + parseInt(this.gap, 10)) + 1
            );
        this.currentScrollPosition = slot.scrollLeft;
      }
      this._currentItem = this._currentItem === 0 ? 1 : this._currentItem;
    }
  }
};
__decorate([event()], SHCarousel.prototype, 'leftArrowClickEvent', void 0);
__decorate([event()], SHCarousel.prototype, 'rightArrowClickEvent', void 0);
__decorate(
  [property({ type: String, reflect: true, attribute: 'element-width' })],
  SHCarousel.prototype,
  'elementWidth',
  void 0
);
__decorate([property({ type: String, reflect: true })], SHCarousel.prototype, 'gap', void 0);
__decorate([property({ type: String, reflect: true })], SHCarousel.prototype, 'snap', void 0);
__decorate([property({ type: Boolean, reflect: true })], SHCarousel.prototype, 'counter', void 0);
__decorate([property({ type: Boolean, reflect: true })], SHCarousel.prototype, 'arrows', void 0);
__decorate(
  [property({ type: String, reflect: true, attribute: 'accessible-next-label' })],
  SHCarousel.prototype,
  'accessibleNextLabel',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'accessible-previous-label' })],
  SHCarousel.prototype,
  'accessiblePreviousLabel',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'counter-type' })],
  SHCarousel.prototype,
  'counterType',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'arrow-placement' })],
  SHCarousel.prototype,
  'arrowPlacement',
  void 0
);
__decorate([state()], SHCarousel.prototype, '_currentItem', void 0);
__decorate([state()], SHCarousel.prototype, '_totalSlotElements', void 0);
__decorate([state()], SHCarousel.prototype, 'reachedEnd', void 0);
SHCarousel = __decorate([customElement('sh-carousel')], SHCarousel);
export { SHCarousel };
